import { z } from "zod";
import { bookSemanticSnapshotArtifactBindingV1Schema, bookSemanticSnapshotAuthorityEvidenceV1Schema, bookSemanticSnapshotSourceBindingV1Schema, } from "./book-production-snapshot.js";
import { bookProductionProductSchema } from "./book-production-evidence.js";
export const BOOK_PRODUCTION_DECISION_CONTRACT = "BOOK_PRODUCTION_DECISION_V1";
export const BOOK_PRODUCTION_DECISION_POINTER_CONTRACT = "BOOK_PRODUCTION_DECISION_POINTER_V1";
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(240)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/u);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
export const bookProductionDecisionKindSchema = z.enum([
    "BEDTIME_CHARACTER_DIRECTION",
    "BEDTIME_GUARDIAN_REVIEW",
    "BEDTIME_FACT_REVIEW",
    "BEDTIME_LIKENESS_REVIEW",
    "BEDTIME_STORY_PROMISE",
    "BEDTIME_VISUAL_PROMISE",
    "BEDTIME_STORY_SHAPE",
    "BEDTIME_MANUSCRIPT",
    "BEDTIME_STORYBOARD",
    "BEDTIME_VISUAL_DIRECTION",
    "BEDTIME_ART_BATCH",
    "BEDTIME_SEQUENCE",
    "BEDTIME_ACCESSIBILITY_DESCRIPTIONS",
    "BEDTIME_DIGITAL_EDITION",
    "BEDTIME_FINAL_PROOF",
    "COMIC_SCRIPT",
    "COMIC_PANEL_PLAN",
    "COMIC_LETTERING",
    "COMIC_READING_ORDER",
    "COMIC_CONTINUITY",
    "COMIC_VISUAL",
    "COMIC_ACCESSIBILITY_DESCRIPTIONS",
    "COMIC_DIGITAL_EDITION",
    "RECIPE_NORMALIZATION",
    "RECIPE_QUANTITIES",
    "RECIPE_INGREDIENT_STEP_RELATIONSHIPS",
    "RECIPE_SAFETY",
    "RECIPE_ALLERGENS",
    "RECIPE_SCALING_STORAGE_DONENESS",
    "RECIPE_ACCESSIBILITY_DESCRIPTIONS",
    "RECIPE_DIGITAL_EDITION",
    "MY_STORY_VOICE_FIDELITY",
    "MY_STORY_EVIDENCE",
    "MY_STORY_PRIVACY",
    "MY_STORY_CORRECTIONS",
    "MY_STORY_UNCERTAINTY",
    "MY_STORY_ACCESSIBILITY_DESCRIPTIONS",
    "MY_STORY_DIGITAL_EDITION",
    "OUR_STORY_ATTRIBUTION",
    "OUR_STORY_REVEAL_POLICY",
    "OUR_STORY_DISAGREEMENT_TREATMENT",
    "OUR_STORY_PRINCIPAL_A_APPROVAL",
    "OUR_STORY_PRINCIPAL_B_APPROVAL",
    "OUR_STORY_JOINT_APPROVAL",
    "OUR_STORY_ACCESSIBILITY_DESCRIPTIONS",
    "OUR_STORY_DIGITAL_EDITION",
]);
export const BOOK_PRODUCTION_DECISION_REQUIREMENTS = {
    BEDTIME: [
        "BEDTIME_STORY_PROMISE",
        "BEDTIME_VISUAL_PROMISE",
        "BEDTIME_CHARACTER_DIRECTION",
        "BEDTIME_GUARDIAN_REVIEW",
        "BEDTIME_FACT_REVIEW",
        "BEDTIME_LIKENESS_REVIEW",
        "BEDTIME_STORY_SHAPE",
        "BEDTIME_MANUSCRIPT",
        "BEDTIME_STORYBOARD",
        "BEDTIME_VISUAL_DIRECTION",
        "BEDTIME_ART_BATCH",
        "BEDTIME_SEQUENCE",
        "BEDTIME_ACCESSIBILITY_DESCRIPTIONS",
        "BEDTIME_DIGITAL_EDITION",
        "BEDTIME_FINAL_PROOF",
    ],
    COMIC: [
        "COMIC_SCRIPT",
        "COMIC_PANEL_PLAN",
        "COMIC_LETTERING",
        "COMIC_READING_ORDER",
        "COMIC_CONTINUITY",
        "COMIC_VISUAL",
        "COMIC_ACCESSIBILITY_DESCRIPTIONS",
        "COMIC_DIGITAL_EDITION",
    ],
    RECIPE: [
        "RECIPE_NORMALIZATION",
        "RECIPE_QUANTITIES",
        "RECIPE_INGREDIENT_STEP_RELATIONSHIPS",
        "RECIPE_SAFETY",
        "RECIPE_ALLERGENS",
        "RECIPE_SCALING_STORAGE_DONENESS",
        "RECIPE_ACCESSIBILITY_DESCRIPTIONS",
        "RECIPE_DIGITAL_EDITION",
    ],
    MY_STORY: [
        "MY_STORY_VOICE_FIDELITY",
        "MY_STORY_EVIDENCE",
        "MY_STORY_PRIVACY",
        "MY_STORY_CORRECTIONS",
        "MY_STORY_UNCERTAINTY",
        "MY_STORY_ACCESSIBILITY_DESCRIPTIONS",
        "MY_STORY_DIGITAL_EDITION",
    ],
    OUR_STORY: [
        "OUR_STORY_ATTRIBUTION",
        "OUR_STORY_REVEAL_POLICY",
        "OUR_STORY_DISAGREEMENT_TREATMENT",
        "OUR_STORY_PRINCIPAL_A_APPROVAL",
        "OUR_STORY_PRINCIPAL_B_APPROVAL",
        "OUR_STORY_JOINT_APPROVAL",
        "OUR_STORY_ACCESSIBILITY_DESCRIPTIONS",
        "OUR_STORY_DIGITAL_EDITION",
    ],
};
const kindsByProduct = Object.fromEntries(Object.entries(BOOK_PRODUCTION_DECISION_REQUIREMENTS).map(([product, kinds]) => [
    product,
    new Set(kinds),
]));
const budgetAuthorizationSchema = z
    .object({
    quoteId: safeIdSchema,
    quoteDigestSha256: sha256Schema,
    quoteRecordLocator: z
        .object({
        partitionKey: z.string().trim().min(1).max(500),
        sortKey: z.string().trim().min(1).max(500),
        revisionAttribute: safeIdSchema,
    })
        .strict(),
    quoteRecordRevision: z.union([
        z.string().trim().min(1).max(160),
        z.number().int().nonnegative(),
    ]),
    provider: z.literal("OPENAI_IMAGE_GEN_2"),
    imageCount: z.number().int().positive().max(100),
    maximumCostMicros: z.number().int().positive().max(1_000_000_000),
    currency: z.literal("USD"),
    expiresAt: timestampSchema,
    dispatch: z
        .object({
        mode: z.literal("SEQUENTIAL"),
        maximumConcurrency: z.literal(1),
        stopNewDispatchOnFailure: z.literal(true),
        ambiguousDispatchRequiresReconciliation: z.literal(true),
    })
        .strict(),
})
    .strict();
/** Acceptance of settled artwork history, never permission for a new provider batch. */
export const completedBookArtworkAuthoritySchema = z
    .object({
    contract: z.literal("COMPLETED_BOOK_ARTWORK_AUTHORITY_V1"),
    artworkAuthorityId: safeIdSchema,
    planDigestSha256: sha256Schema,
    recordLocator: z
        .object({
        partitionKey: z.string().trim().min(1).max(500),
        sortKey: z.string().trim().min(1).max(500),
        revisionAttribute: safeIdSchema,
    })
        .strict(),
    recordRevision: z.union([z.string().trim().min(1).max(160), z.number().int().positive()]),
    recordChecksumSha256: sha256Schema,
    requiredUseIds: z.array(safeIdSchema).min(1).max(32),
    assetArtifactBindingIds: z.array(safeIdSchema).min(1).max(32),
    coverArtifactBindingId: safeIdSchema,
    completedActionIds: z.array(safeIdSchema).min(1).max(32),
    providerDispatchAuthorized: z.literal(false),
})
    .strict()
    .superRefine((authority, context) => {
    for (const key of ["requiredUseIds", "assetArtifactBindingIds", "completedActionIds"])
        if (new Set(authority[key]).size !== authority[key].length)
            context.addIssue({
                code: "custom",
                path: [key],
                message: "Completed artwork authority cannot contain duplicate identities.",
            });
    if (authority.requiredUseIds.length !== authority.assetArtifactBindingIds.length ||
        authority.assetArtifactBindingIds.includes(authority.coverArtifactBindingId))
        context.addIssue({
            code: "custom",
            path: ["assetArtifactBindingIds"],
            message: "Every required media use and the separate cover need their exact artifact bindings.",
        });
});
const decisionInputSchema = z
    .object({
    product: bookProductionProductSchema,
    kind: bookProductionDecisionKindSchema,
    expectedBookRevision: z.number().int().nonnegative(),
    expectedDecisionRevision: z.number().int().nonnegative(),
    sourceBindings: z.array(bookSemanticSnapshotSourceBindingV1Schema).min(1).max(12),
    artifactBindings: z.array(bookSemanticSnapshotArtifactBindingV1Schema).max(32),
    authorityEvidence: z.array(bookSemanticSnapshotAuthorityEvidenceV1Schema).max(25),
    selectedIds: z.array(safeIdSchema).min(1).max(50),
    note: z.string().trim().min(1).max(2_000).nullable(),
    budgetAuthorization: budgetAuthorizationSchema.nullable(),
    // Optional with no default: historical command digests and replay identities stay unchanged.
    completedArtworkAuthority: completedBookArtworkAuthoritySchema.nullable().optional(),
})
    .strict()
    .superRefine((input, context) => {
    if (!kindsByProduct[input.product].has(input.kind))
        context.addIssue({
            code: "custom",
            path: ["kind"],
            message: "The production decision kind does not belong to this book family.",
        });
    const isArtBatch = input.kind === "BEDTIME_ART_BATCH";
    if (isArtBatch !== Boolean(input.budgetAuthorization || input.completedArtworkAuthority) ||
        Boolean(input.budgetAuthorization && input.completedArtworkAuthority))
        context.addIssue({
            code: "custom",
            path: ["budgetAuthorization"],
            message: "Bedtime artwork acceptance requires exactly one current budget authorization or completed-art authority; other decisions cannot include either.",
        });
    if (new Set(input.sourceBindings.map(({ bindingId }) => bindingId)).size !==
        input.sourceBindings.length ||
        new Set(input.artifactBindings.map(({ bindingId }) => bindingId)).size !==
            input.artifactBindings.length ||
        new Set(input.authorityEvidence.map(({ evidenceId }) => evidenceId)).size !==
            input.authorityEvidence.length ||
        new Set(input.selectedIds).size !== input.selectedIds.length)
        context.addIssue({
            code: "custom",
            path: ["selectedIds"],
            message: "Production decisions cannot contain duplicate exact bindings or selections.",
        });
    const transactionRecords = input.sourceBindings.length +
        input.artifactBindings.length +
        input.authorityEvidence.length +
        (input.budgetAuthorization ? 1 : 0) +
        (input.completedArtworkAuthority ? 1 : 0);
    if (transactionRecords > 45)
        context.addIssue({
            code: "custom",
            path: ["sourceBindings"],
            message: "One production decision may bind at most forty-five transactional records.",
        });
});
export const createBookProductionDecisionCommandSchema = decisionInputSchema;
export const bookProductionDecisionV1Schema = decisionInputSchema
    .omit({ expectedBookRevision: true, expectedDecisionRevision: true })
    .extend({
    contract: z.literal(BOOK_PRODUCTION_DECISION_CONTRACT),
    decisionId: safeIdSchema,
    bookId: safeIdSchema,
    sourceRevision: z.number().int().nonnegative(),
    decisionRevision: z.number().int().positive(),
    state: z.literal("ACCEPTED"),
    decidedBy: safeIdSchema,
    decidedAt: timestampSchema,
    requestHashSha256: sha256Schema,
    truthBoundary: z.literal("CURRENT_CUSTOMER_DECISION_NOT_RENDER_PRINT_APPROVAL_PROVIDER_DISPATCH_OR_PHYSICAL_PROOF"),
})
    .strict();
export const bookProductionDecisionPointerV1Schema = z
    .object({
    contract: z.literal(BOOK_PRODUCTION_DECISION_POINTER_CONTRACT),
    bookId: safeIdSchema,
    product: bookProductionProductSchema,
    kind: bookProductionDecisionKindSchema,
    decisionId: safeIdSchema,
    decisionRevision: z.number().int().positive(),
    sourceRevision: z.number().int().nonnegative(),
    requestHashSha256: sha256Schema,
    updatedAt: timestampSchema,
})
    .strict();
//# sourceMappingURL=book-production-decision.js.map